import { useEffect, useRef, useState, lazy, Suspense } from "react";
import "./App.css";
import "./theme.css";
import "./song-tabs.css";
import "./resource-pages.css";
import "./cookie-consent.css";
import { ProCheckoutResumeStandalone } from "./components/ProCheckoutResume";
import { readProCheckoutIntent } from "./lib/proCheckout";
import {
  Gift,
  Mail,
  PlayCircle,
  CheckCircle2,
  Music2,
  Lock,
  Sparkles,
  ArrowRight,
  ChevronDown,
  Moon,
  Sun,
  MessageCircle,
  Send,
  X,
  Coins,
  Flame,
  Guitar,
  Hand,
  Drum,
  GraduationCap,
  Crown,
  Heart,
  Users,
} from "lucide-react";
import AccountPage from "./pages/Account";
// Lazy-loaded: pure content routes that aren't on the homepage critical path, so
// they ship as separate chunks instead of bloating the initial bundle. Payment
// and account routes stay eagerly imported; the large member experiences load
// only when their route (or the homepage member preview) is actually rendered.
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Members = lazy(() => import("./pages/Members"));
const MembersPro = lazy(() => import("./pages/MembersPro"));
// Lazy-loaded: the standalone interactive tool pages (/tuner, /metronome,
// /chords) are not on the homepage critical path, so they ship as their own
// chunk instead of bloating the initial bundle.
const ChordsPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.ChordsPage })));
const GuitarChordBuzzingFixerPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.GuitarChordBuzzingFixerPage })));
const MetronomePage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.MetronomePage })));
const OneMinuteChordChangesPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.OneMinuteChordChangesPage })));
const PianoGuitarNotesPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.PianoGuitarNotesPage })));
const TapTempoPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.TapTempoPage })));
const TunerPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.TunerPage })));
const VocalRangeGuitarKeyPage = lazy(() => import("./pages/ToolPages").then((m) => ({ default: m.VocalRangeGuitarKeyPage })));
import { useAccountAutosave } from "./hooks/useAccountAutosave";
import { supabase } from "./lib/supabaseClient";

const GA_MEASUREMENT_ID = "G-42HBW6VFLK";
// Clarity project IDs are public identifiers. Loading remains consent-controlled below.
const CLARITY_PROJECT_ID = "xbmk5es6zo";
const THEME_STORAGE_KEY = "guitar_with_rene_theme_light_v1";
const SIGNUP_ATTENTION_EVENT = "guitar_with_rene_signup_attention";

const KIT_FORM_UID = "b602896c16";
const KIT_FORM_ID = "9386699";
const KIT_FORM_SCRIPT_SRC =
  "https://guitar-with-rene.kit.com/b602896c16/index.js";

const HERO_EMAIL_INPUT_ID = "hero-email-address";

function MemberRouteFallback() {
  return (
    <div className="page-shell light-page members-route-fallback" aria-busy="true" aria-label="Loading Guitar With Rene">
      <div className="members-route-fallback-head">
        <span className="members-route-fallback-brand" />
        <span className="members-route-fallback-menu" />
      </div>
      <div className="members-route-fallback-nav">
        <span /><span /><span /><span />
      </div>
      <div className="members-route-fallback-card">
        <span /><span /><span />
      </div>
    </div>
  );
}

const LINKS = {
  heroSignup: "#hero-signup",
  courseSection: "#course-section",
  // One product now: the full course is Pro, bought once (was the Gumroad course).
  course: "/pro",
  freeLesson: "/member/course/free",
  members: "/member",
  pro: "/pro",
  home: "/",
  welcome: "/welcome",
  feedback: "mailto:rene@guitarwithrene.com?subject=Guitar%20With%20Rene%20feedback",
  privacy: "/privacy",
  terms: "/terms",
};

const ASSETS = {
  face: "/rene-face.webp",
  freePack: "/free-pack-preview.webp",
  lessonVideo: "/free-beginner-lesson.mp4",
  lessonPoster: "/free-beginner-lesson-poster.webp",
  membersPreview: "/members-today-preview.png",
  tab: "/tab-image.webp",
  course: "/beginner-course-cover.webp",
};

const roadmapItems = [
  {
    range: "Day 1",
    title: "Read chord diagrams and play for 10 minutes",
    locked: false,
  },
  {
    range: "Days 2 and 3",
    title: "Your first chords: Em, G, C, D",
    locked: true,
  },
  {
    range: "Days 4 and 5",
    title: "Two strumming patterns with G, D, Em and C",
    locked: true,
  },
  {
    range: "Days 6 and 7",
    title: "Four more chords and your first tab warm up",
    locked: true,
  },
];

const faqItems = [
  {
    question: "Is this for total beginners?",
    answer:
      "Yes. It is made for people starting from zero. You do not need to know chords, read music, or understand theory yet.",
  },
  {
    question: "What exactly do I get?",
    answer:
      "You get the 7 day beginner course, a Day 1 video, tappable lesson pages, your first 8 chords, two strumming patterns, common beginner mistake fixes, and tab basics.",
  },
  {
    question: "Do I need a specific guitar?",
    answer:
      "No. Acoustic or electric both work. Any playable guitar is enough for the first 7 days. No amp, pedals, or extra gear needed.",
  },
  {
    question: "Is this just a newsletter?",
    answer:
      "No. The first thing you get is the interactive course link and starter lesson. After that I may send beginner guitar tips. You can unsubscribe anytime.",
  },
  {
    question: "What happens after I enter my email?",
    answer:
      "You get the course link immediately. Open it, start Day 1, and keep the page saved so you can come back to your XP and sections.",
  },
  {
    question: "How is this different from YouTube tutorials?",
    answer:
      "YouTube gives you thousands of videos with no clear order. This gives you one path for the first week, with one small task each day.",
  },
  {
    question: "What's the difference between the free course and the full course?",
    answer:
      "The free 7 day course gets you switching your first chords and strumming along. The full course (Pro) keeps going for 55 more guided days: tab reading, hammer-ons, muting, your first riff, barre chords, lead guitar, fingerstyle and simple theory, with the practice tools built into every lesson. It is €29, paid once.",
  },
  {
    question: "What is Pro, and do I need it?",
    answer:
      "No, you don't need it to start. Pro is the full course for after the free week: €29 once, no subscription. It unlocks the rest of the guided path plus song practice, Tone Lab, recording, and songwriting tools, and it stays unlocked. There is a 14-day money-back guarantee.",
  },
];

function getStoredTheme() {
  if (typeof window === "undefined") {
    return "light";
  }

  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (storedTheme === "dark" || storedTheme === "light") {
      return storedTheme;
    }
  } catch {
    return "light";
  }

  // No saved choice: follow the OS/browser preference on first visit.
  try {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  } catch {
    return "light";
  }

  return "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.setAttribute("data-theme", theme);
  document.body.classList.toggle("light-page-active", theme === "light");
  document.body.classList.toggle("dark-page-active", theme === "dark");
}

function sendAnalyticsPageView() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    send_to: GA_MEASUREMENT_ID,
  });
}

// Generic GA4 event. Consent mode gates delivery, so calling this before the
// visitor accepts cookies is safe (the event is simply not sent). Used to map
// the activation funnel: open mission -> day complete -> account -> course -> pro.
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", name, { send_to: GA_MEASUREMENT_ID, ...params });
}

function isLikelySocialInAppBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /tiktok|musical_ly|instagram|fb_iab|fbav|line|pinterest/i.test(
    navigator.userAgent || "",
  );
}

function getDesignVariant() {
  if (typeof window === "undefined") {
    return "cool";
  }

  try {
    return new URLSearchParams(window.location.search).get("v") === "warm"
      ? "warm"
      : "cool";
  } catch {
    return "cool";
  }
}

function trackSignupLead() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", "free_pack_signup", {
    send_to: GA_MEASUREMENT_ID,
    event_category: "lead",
    event_label: "hero_free_7_day_plan",
    form_name: "hero_free_7_day_plan",
    design_variant: getDesignVariant(),
  });
}

const CONSENT_STORAGE_KEY = "gwrene_analytics_consent";

function loadClarity() {
  if (
    typeof window === "undefined" ||
    !CLARITY_PROJECT_ID ||
    document.querySelector('script[data-gwrene-clarity="true"]')
  ) {
    return;
  }

  window.clarity = window.clarity || function clarity() {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  script.dataset.gwreneClarity = "true";
  document.head.appendChild(script);
}

function applyAnalyticsConsent(granted) {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
    });
  }

  if (granted) {
    loadClarity();
  }
}

function CookieConsent() {
  // 2026-09-30: Rene asked for the banner to wait until the visitor scrolls, so
  // the hero and "Start free course" are seen clean first. Trade-off: visitors
  // who bounce without scrolling are never asked, so analytics undercount them.
  // Pages too short to scroll show it after a few seconds instead.
  const [scrolled, setScrolled] = useState(false);
  const [undecided, setUndecided] = useState(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      return stored !== "granted" && stored !== "denied";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!undecided || scrolled || typeof window === "undefined") return undefined;
    const THRESHOLD = 240;
    const check = () => {
      if (window.scrollY > THRESHOLD) setScrolled(true);
    };
    window.addEventListener("scroll", check, { passive: true });
    const shortPageTimer = window.setTimeout(() => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= THRESHOLD) setScrolled(true);
    }, 4000);
    check();
    return () => {
      window.removeEventListener("scroll", check);
      window.clearTimeout(shortPageTimer);
    };
  }, [undecided, scrolled]);

  const visible = undecided && scrolled;

  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
    if (stored === "granted") {
      applyAnalyticsConsent(true);
    }
  }, []);

  // While the bottom-pinned banner is visible, mark the body so the page can
  // reserve space and the fixed banner never sits on top of the primary CTA.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("cookie-banner-open", visible);
    return () => {
      document.body.classList.remove("cookie-banner-open");
    };
  }, [visible]);

  function decide(granted) {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, granted ? "granted" : "denied");
    } catch {
      // ignore storage errors
    }
    applyAnalyticsConsent(granted);
    if (granted) {
      // count this session now that tracking is allowed
      sendAnalyticsPageView();
    }
    setUndecided(false);
  }

  if (!visible) {
    return null;
  }

  return (
    <div className="cookie-banner gwr-consent" role="dialog" aria-label="Cookie consent">
      <img className="gwr-consent-art" src="/cookie-guitar.webp" alt="" width="56" height="56" />
      <div className="cookie-banner-copy">
        <h2>Help make the lessons better?</h2>
        <p>
          Analytics show which lessons help and where people get stuck. The lessons work either way.{" "}
          <a href="/privacy">Privacy</a>
        </p>
      </div>
      <div className="cookie-banner-actions">
        <button type="button" className="cookie-button" onClick={() => decide(false)}>
          Decline
        </button>
        <button type="button" className="cookie-button primary" onClick={() => decide(true)}>
          Accept
        </button>
      </div>
    </div>
  );
}

function ensureEmailIconFixStyles() {
  if (typeof document === "undefined") {
    return;
  }

  if (document.getElementById("gwr-email-icon-fix-styles")) {
    return;
  }

  const style = document.createElement("style");
  style.id = "gwr-email-icon-fix-styles";
  style.textContent = `
    #hero-signup .email-field-wrap::before,
    #hero-signup .email-field-with-icon::before,
    #hero-signup .kit-form-embed .formkit-field::before {
      content: none !important;
      display: none !important;
    }

    #hero-signup .kit-form-embed .formkit-field,
    #hero-signup .kit-form-embed .email-field-with-icon {
      position: relative !important;
    }

    #hero-signup .kit-form-embed .custom-email-icon {
      position: absolute !important;
      left: 26px !important;
      top: 50% !important;
      z-index: 9999 !important;
      transform: translateY(-50%) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 22px !important;
      height: 22px !important;
      color: #5e5ce6 !important;
      pointer-events: none !important;
    }

    #hero-signup .kit-form-embed .custom-email-icon svg {
      display: block !important;
      width: 20px !important;
      height: 20px !important;
      color: #5e5ce6 !important;
      stroke: #5e5ce6 !important;
      filter: drop-shadow(0 0 8px rgba(94, 92, 230, 0.28)) !important;
    }

    #hero-signup .kit-form-embed .formkit-field > svg,
    #hero-signup .kit-form-embed .formkit-field > .formkit-icon,
    #hero-signup .kit-form-embed .formkit-field > [class*="icon"]:not(.custom-email-icon),
    #hero-signup .kit-form-embed .formkit-field > [class*="Icon"]:not(.custom-email-icon) {
      display: none !important;
    }

    #hero-signup .kit-form-embed input[type="email"],
    #hero-signup .kit-form-embed input[name="email"],
    #hero-signup .kit-form-embed .formkit-input {
      padding-left: 64px !important;
      background-image: none !important;
      color: #111318 !important;
      -webkit-text-fill-color: #111318 !important;
    }

    #hero-signup .kit-form-embed input[type="email"]::placeholder,
    #hero-signup .kit-form-embed input[name="email"]::placeholder,
    #hero-signup .kit-form-embed .formkit-input::placeholder {
      color: #687181 !important;
      -webkit-text-fill-color: #687181 !important;
      opacity: 1 !important;
    }
  `;

  document.head.appendChild(style);
}

function updateKitButtonText(container, { isNewsletter = false } = {}) {
  if (!container) {
    return;
  }

  ensureEmailIconFixStyles();

  const centeredFormElements = container.querySelectorAll(
    [
      "form",
      ".formkit-form",
      ".formkit-fields",
      ".formkit-fieldset",
      ".formkit-input-group",
      ".formkit-field",
      ".email-field-with-icon",
      'input[type="email"]',
      'input[name="email"]',
      ".formkit-input",
      "button",
      'input[type="submit"]',
      ".formkit-submit",
    ].join(","),
  );

  centeredFormElements.forEach((element) => {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    element.style.width = "292px";
    element.style.maxWidth = "100%";
    element.style.marginLeft = "auto";
    element.style.marginRight = "auto";
  });

  const submitElements = container.querySelectorAll(
    'button, input[type="submit"], .formkit-submit',
  );

  submitElements.forEach((element) => {
    const plainText = isNewsletter ? "Join the free newsletter" : "Start the Free Course";

    if (element instanceof HTMLInputElement) {
      if (element.value !== plainText) {
        element.value = plainText;
      }

      return;
    }

    const submitIconSvg = isNewsletter
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px; flex-shrink: 0;"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px; flex-shrink: 0;"><rect x="3" y="8" width="18" height="4" rx="1"></rect><path d="M12 8v13"></path><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path><path d="M7.5 8a2.5 2.5 0 0 1 0-5c3.5 0 4.5 5 4.5 5"></path><path d="M16.5 8a2.5 2.5 0 0 0 0-5c-3.5 0-4.5 5-4.5 5"></path></svg>`;
    const targetHTML = `${submitIconSvg}<span>${plainText}</span>`;

    const hasSvg = element.querySelector("svg");
    const hasCurrentText = element.textContent?.includes(plainText);

    if (!hasSvg || !hasCurrentText) {
      element.innerHTML = targetHTML;
    }
  });

  const emailInputs = container.querySelectorAll(
    'input[type="email"], input[name="email"], .formkit-input',
  );

  emailInputs.forEach((element) => {
    if (!(element instanceof HTMLInputElement)) {
      return;
    }

    element.id = HERO_EMAIL_INPUT_ID;

    const emailPlaceholder = isNewsletter ? "Email address" : "Your email address";
    if (element.placeholder !== emailPlaceholder) {
      element.placeholder = emailPlaceholder;
      element.setAttribute("placeholder", emailPlaceholder);
    }

    element.setAttribute(
      "aria-label",
      isNewsletter
        ? "Email address for Rene's free newsletter"
        : "Email address for the free 7 day beginner guitar practice plan",
    );

    element.setAttribute("autocomplete", "email");
    element.setAttribute("inputmode", "email");
    element.setAttribute("autocapitalize", "none");
    element.setAttribute("spellcheck", "false");
    element.setAttribute("enterkeyhint", "done");
    element.setAttribute("required", "true");

    const fieldWrapper =
      element.closest(".formkit-field") ||
      element.parentElement ||
      element.closest("div");

    if (fieldWrapper) {
      fieldWrapper.classList.add("email-field-with-icon");

      if (!(fieldWrapper instanceof HTMLElement)) {
        return;
      }

      fieldWrapper.style.position = "relative";

      let icon = fieldWrapper.querySelector(".custom-email-icon");

      if (!icon) {
        icon = document.createElement("span");
        icon.className = "custom-email-icon";
        icon.setAttribute("aria-hidden", "true");
        icon.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.35" stroke-linecap="round" stroke-linejoin="round">
            <rect width="20" height="16" x="2" y="4" rx="2"></rect>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
          </svg>
        `;

        fieldWrapper.prepend(icon);
      }

      if (icon instanceof HTMLElement) {
        icon.style.position = "absolute";
        icon.style.left = "26px";
        icon.style.top = "50%";
        icon.style.zIndex = "9999";
        icon.style.transform = "translateY(-50%)";
        icon.style.display = "flex";
        icon.style.alignItems = "center";
        icon.style.justifyContent = "center";
        icon.style.width = "22px";
        icon.style.height = "22px";
        icon.style.color = "#5e5ce6";
        icon.style.pointerEvents = "none";
      }

      element.style.paddingLeft = "64px";
      element.style.backgroundImage = "none";
      element.style.color = "#111318";
      element.style.webkitTextFillColor = "#111318";
    }
  });
}

function focusHeroSignupEmail(event) {
  if (event) {
    event.preventDefault();
  }

  const signupElement = document.querySelector(LINKS.heroSignup);

  const requestSignupAttention = () => {
    window.dispatchEvent(new CustomEvent(SIGNUP_ATTENTION_EVENT));
  };

  requestSignupAttention();

  if (signupElement) {
    signupElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    try {
      window.history.replaceState(null, "", LINKS.heroSignup);
    } catch {
      // Ignore history errors.
    }
  }

  function focusInput() {
    const emailInput = document.querySelector(
      '#hero-signup input[type="email"], #hero-signup input[name="email"], #hero-signup .formkit-input',
    );

    if (emailInput instanceof HTMLElement) {
      emailInput.focus({ preventScroll: true });
    }
  }

  window.setTimeout(focusInput, 520);
  window.setTimeout(requestSignupAttention, 560);
  window.setTimeout(focusInput, 940);
}

function ThemeToggle({ theme, onToggle }) {
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={onToggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? (
        <Sun size={18} strokeWidth={2.35} />
      ) : (
        <Moon size={18} strokeWidth={2.35} />
      )}
    </button>
  );
}

function InAppBrowserHint() {
  const [showHint] = useState(() => isLikelySocialInAppBrowser());

  if (!showHint) {
    return null;
  }

  return (
    <p className="in-app-browser-hint">
      TikTok browser can hide autofill. Open in Safari or Chrome if typing feels
      broken.
    </p>
  );
}

function AnimatedCount({ target, suffix = "" }) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const reducedMotionTimer = window.setTimeout(() => setValue(target), 0);
      return () => window.clearTimeout(reducedMotionTimer);
    }

    let frame;
    let start;
    const duration = 1400;

    function tick(now) {
      if (start === undefined) {
        start = now;
      }

      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(Math.round(eased * target));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    }

    const delayTimer = window.setTimeout(() => {
      frame = requestAnimationFrame(tick);
    }, 450);

    return () => {
      window.clearTimeout(delayTimer);
      cancelAnimationFrame(frame);
    };
  }, [target]);

  return (
    <strong>
      {value}
      {suffix}
    </strong>
  );
}

function SectionBadge({ icon: Icon, children }) {
  return (
    <div className="section-badge">
      <Icon size={18} strokeWidth={2.25} />
      <span>{children}</span>
    </div>
  );
}

function ButtonLink({
  href,
  children,
  variant = "primary",
  icon: Icon,
  onClick,
}) {
  const isExternalLink = href.startsWith("http");

  return (
    <a
      className={`button-link ${variant}`}
      href={href}
      target={isExternalLink ? "_blank" : undefined}
      rel={isExternalLink ? "noreferrer" : undefined}
      onClick={onClick}
    >
      {Icon ? <Icon size={18} strokeWidth={2.25} /> : null}
      <span>{children}</span>
      {variant === "primary" ? <ArrowRight size={18} /> : null}
    </a>
  );
}

function RoadmapRow({ range, title, locked }) {
  return (
    <button
      type="button"
      className={`roadmap-row ${locked ? "is-locked" : "is-unlocked"}`}
      onClick={focusHeroSignupEmail}
      aria-label={
        locked
          ? `${range}: ${title}. Enter your email to unlock the free course.`
          : `${range}: ${title}. Included in the free starter video.`
      }
    >
      <span className="roadmap-range">{range}</span>
      <span className="roadmap-title">{title}</span>

      {locked ? (
        <Lock size={16} strokeWidth={2.4} className="roadmap-icon" />
      ) : (
        <PlayCircle size={18} strokeWidth={2.4} className="roadmap-icon" />
      )}
    </button>
  );
}

function AccordionCard({ title, children, className = "", defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <div className={`accordion-card ${className}`}>
      <button
        type="button"
        className="accordion-toggle"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="accordion-title-wrap">
          <span>{title}</span>
        </span>

        <ChevronDown
          size={22}
          strokeWidth={2.4}
          className={`accordion-chevron ${isOpen ? "open" : ""}`}
        />
      </button>

      {isOpen ? (
        <div id={contentId} className="accordion-content">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function FAQCard({ question, answer, defaultOpen = false }) {
  return (
    <AccordionCard
      title={question}
      className="faq-accordion-card"
      defaultOpen={defaultOpen}
    >
      <p>{answer}</p>
    </AccordionCard>
  );
}

function BeginnerTrustSection() {
  return (
    <section className="content-section about-rene-section compact-section">
      <div className="about-rene-card">
        <img
          src={ASSETS.face}
          alt="Rene from Guitar With Rene"
          loading="lazy"
          decoding="async"
        />

          <div>
          <SectionBadge icon={Sparkles}>Made by Rene</SectionBadge>
          <p>
            I lost a lot of time jumping between random lessons. This is the
            simple order I wish I had when I started.
          </p>
        </div>
      </div>
    </section>
  );
}

function LearnSection() {
  return (
    <section className="content-section learn-section compact-section inside-guide-section">
      <SectionBadge icon={Sparkles}>Free course</SectionBadge>
      <h2>Your first 7 days are planned</h2>

      <p className="learn-outcome-line">
        By Day 7, you will be switching{" "}
        <strong>G, D, Em and C</strong>. Those four chords are used in
        Knockin' on Heaven's Door, Let Her Go, and hundreds more.
      </p>

      <div className="day-roadmap">
        {roadmapItems.map((item) => (
          <RoadmapRow
            key={item.range}
            range={item.range}
            title={item.title}
            locked={item.locked}
          />
        ))}
      </div>

      <a
        className="button-link primary roadmap-unlock-cta"
        href={LINKS.heroSignup}
        onClick={focusHeroSignupEmail}
      >
        <Gift size={16} strokeWidth={2.5} />
        <span>Start the Free Course</span>
        <ArrowRight size={15} strokeWidth={2.4} />
      </a>
    </section>
  );
}

export function SiteFooter({ showCta = false }) {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        {showCta ? (
          <div className="footer-cta">
            <a
              className="button-link primary"
              href={LINKS.heroSignup}
              onClick={focusHeroSignupEmail}
            >
              <Gift size={20} strokeWidth={2.5} />
              <span>Start the Free Course</span>
              <ArrowRight size={18} />
            </a>
          </div>
        ) : null}

        <details className="footer-guides">
          <summary>
            <span className="footer-guides-icon" aria-hidden="true">
              <Guitar size={19} strokeWidth={2.35} />
            </span>
            <span className="footer-guides-summary-copy">
              <strong>Explore free beginner guides</strong>
              <small>Songs, chords, tabs, tools, and a simple practice plan</small>
            </span>
            <span className="footer-guides-count">6 guides</span>
            <ChevronDown className="footer-guides-chevron" size={18} strokeWidth={2.4} aria-hidden="true" />
          </summary>
          <nav className="footer-guide-links" aria-label="Free beginner guitar guides">
            <a href="/easy-guitar-songs/"><strong>Easy songs</strong><span>Start playing recognizable music</span></a>
            <a href="/easy-guitar-tabs/"><strong>Easy tabs</strong><span>Short riffs for complete beginners</span></a>
            <a href="/beginner-guitar-chords/"><strong>Beginner chords</strong><span>Learn the essential open shapes</span></a>
            <a href="/how-to-read-guitar-tabs/"><strong>How to read tabs</strong><span>Understand the lines and numbers</span></a>
            <a href="/guitar-practice-plan/"><strong>Practice plan</strong><span>Know what to practise next</span></a>
            <a href="/free-guitar-tools/"><strong>Free guitar tools</strong><span>Tempo, notes, chord fixes, and singing key</span></a>
          </nav>
        </details>

        <div className="footer-meta">
          <nav className="footer-primary-links" aria-label="Guitar With Rene">
            <a href={LINKS.home}>Home</a>
            <a href="/about-rene/">About Rene</a>
            <a href={LINKS.freeLesson}>Free 7-day course</a>
            <a
              href="https://www.tiktok.com/@guitarwithrene"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Guitar With Rene on TikTok"
              className="footer-tiktok-link"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" style={{ flex: "0 0 auto" }}>
                <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1-2.6-2.6c0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3c-1.36-.09-2.59-.66-3.24-1.48z"/>
              </svg>
              TikTok
            </a>
          </nav>
          <nav className="footer-utility-links" aria-label="Help and legal">
            <a href={LINKS.welcome}>How the free course works</a>
            <a href={LINKS.feedback}>Send feedback</a>
            <a href={LINKS.privacy}>Privacy</a>
            <a href={LINKS.terms}>Terms</a>
          </nav>
          <p className="footer-copyright">© 2026 Guitar With Rene</p>
        </div>
      </div>
    </footer>
  );
}


function KitInlineForm({ variant = "course", buttonLabel = "" } = {}) {
  const isNewsletter = variant === "newsletter";
  const formContainerRef = useRef(null);
  const formCardRef = useRef(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showFallbackForm, setShowFallbackForm] = useState(false);
  const [emailReady, setEmailReady] = useState(false);
  const [signupSending, setSignupSending] = useState(false);
  const trackedSignupRef = useRef(false);
  const submittedAttemptRef = useRef(false);

  useEffect(() => {
    const container = formContainerRef.current;

    if (!container) {
      return undefined;
    }

    function hasRenderedKitForm() {
      return Boolean(
        container.querySelector(
          'form, input[type="email"], input[name="email"], .formkit-input, .formkit-submit',
        ),
      );
    }

    function appendKitScript({ forceFresh = false } = {}) {
      if (!forceFresh && container.querySelector(`script[data-uid="${KIT_FORM_UID}"]`)) {
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.dataset.uid = KIT_FORM_UID;
      script.src = forceFresh
        ? `${KIT_FORM_SCRIPT_SRC}?reload=${Date.now()}`
        : KIT_FORM_SCRIPT_SRC;

      container.appendChild(script);
    }

    if (!hasRenderedKitForm()) {
      appendKitScript();
    } else {
      updateKitButtonText(container, { isNewsletter });
      window.setTimeout(() => updateKitButtonText(container, { isNewsletter }), 1200);
      window.setTimeout(() => updateKitButtonText(container, { isNewsletter }), 2500);
    }

    const retryTimer = window.setTimeout(() => {
      if (hasRenderedKitForm()) {
        updateKitButtonText(container, { isNewsletter });
        return;
      }

      container
        .querySelectorAll(`script[data-uid="${KIT_FORM_UID}"]`)
        .forEach((scriptElement) => scriptElement.remove());

      appendKitScript({ forceFresh: true });
    }, 1600);

    const fallbackTimer = window.setTimeout(() => {
      setShowFallbackForm(!hasRenderedKitForm());
    }, 3200);

    let tries = 0;
    const maxTries = 160;

    const interval = window.setInterval(() => {
      tries += 1;
      updateKitButtonText(container, { isNewsletter });

      if (tries >= maxTries) {
        window.clearInterval(interval);
      }
    }, 250);

    const observer = new MutationObserver(() => {
      updateKitButtonText(container, { isNewsletter });

      if (hasRenderedKitForm()) {
        setShowFallbackForm(false);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      window.clearTimeout(retryTimer);
      window.clearTimeout(fallbackTimer);
      window.clearInterval(interval);
      observer.disconnect();
    };
  }, [isNewsletter]);

  useEffect(() => {
    const formCard = formCardRef.current;

    if (!formCard) {
      return undefined;
    }

    function emailLooksValid() {
      const emailInput = formCard.querySelector(
        'input[type="email"], input[name="email"], .formkit-input',
      );

      const emailValue = String(emailInput?.value || "").trim();

      return emailValue.includes("@") && emailValue.includes(".");
    }

    function markSignupSuccess() {
      if (trackedSignupRef.current) {
        return;
      }

      trackedSignupRef.current = true;
      trackSignupLead();
      setShowSuccessPopup(true);
    }

    function hasKitSuccessMessage() {
      const successElement = formCard.querySelector(
        '.formkit-alert-success, [data-element="success"], .formkit-success',
      );

      if (successElement) {
        return true;
      }

      const formText = formCard.textContent?.toLowerCase() || "";

      return (
        formText.includes("check your inbox") ||
        formText.includes("you are all set") ||
        formText.includes("success") ||
        formText.includes("thank you") ||
        formText.includes("subscribed")
      );
    }

    const observer = new MutationObserver(() => {
      if (submittedAttemptRef.current && hasKitSuccessMessage()) {
        markSignupSuccess();
      }
    });

    function handleSubmit() {
      submittedAttemptRef.current = emailLooksValid();

      window.setTimeout(() => {
        if (submittedAttemptRef.current && hasKitSuccessMessage()) {
          markSignupSuccess();
        }
      }, 1200);
    }

    formCard.addEventListener("submit", handleSubmit, true);
    observer.observe(formCard, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      formCard.removeEventListener("submit", handleSubmit, true);
      observer.disconnect();
    };
  }, []);

  // Try a direct JSON submit first so "success" is real, not assumed after a
  // timer. If the request fails (network, CORS change, timeout), fall back to
  // the classic hidden-iframe post so no signup is ever lost — but track that
  // path under a separate event name, keeping the lead metric honest.
  async function handleNativeSignupSubmit(event) {
    event.preventDefault();
    if (signupSending) {
      return;
    }
    const form = event.currentTarget;
    const emailValue = String(new FormData(form).get("email_address") || "").trim();
    if (!emailValue) {
      return;
    }
    setSignupSending(true);
    try {
      const body = new FormData();
      body.set("email_address", emailValue);
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 6000);
      const response = await fetch(form.action, {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      window.clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`Kit responded ${response.status}`);
      }
      if (!trackedSignupRef.current) {
        trackedSignupRef.current = true;
        trackSignupLead();
      }
      setShowSuccessPopup(true);
    } catch {
      submittedAttemptRef.current = true;
      try {
        form.submit();
      } catch {
        // Best effort; the popup below still points the user at the course.
      }
      if (!trackedSignupRef.current) {
        trackedSignupRef.current = true;
        trackEvent("free_pack_signup_unconfirmed", {
          design_variant: getDesignVariant(),
        });
      }
      setShowSuccessPopup(true);
    } finally {
      setSignupSending(false);
    }
  }

  return (
    <>
      <div
        className={`hero-kit-form-block${showSuccessPopup ? " is-success-active" : ""}`}
        ref={formCardRef}
      >
        {!isNewsletter && (
          <div className="hero-free-includes" aria-label="What free access includes">
            <span className="hero-free-includes-title">
              <CheckCircle2 size={14} strokeWidth={2.6} /> Start free with
            </span>
            <ul className="hero-free-perks">
              <li><CheckCircle2 size={14} strokeWidth={2.6} /> 7 day beginner course</li>
              <li><CheckCircle2 size={14} strokeWidth={2.6} /> Day 1 starter video</li>
              <li><CheckCircle2 size={14} strokeWidth={2.6} /> Tuner and metronome</li>
              <li><CheckCircle2 size={14} strokeWidth={2.6} /> Chord library with audio</li>
            </ul>
          </div>
        )}

        <div className="email-field-wrap">
          <div
            ref={formContainerRef}
            className="kit-form-embed hidden-kit-form-embed"
            aria-hidden="true"
            aria-label={isNewsletter ? "Rene's free newsletter signup form" : "Free beginner guitar course email signup form"}
          />

          <iframe
            title="Free plan signup submission"
            name="kit-signup-frame"
            className="kit-submit-frame"
          />

          <form
            className={`kit-form-fallback hero-native-signup-form ${
              showFallbackForm ? "is-fallback-active" : "is-native-active"
            }`}
            action={`https://app.convertkit.com/forms/${KIT_FORM_ID}/subscriptions`}
            method="post"
            target="kit-signup-frame"
            onSubmit={handleNativeSignupSubmit}
          >
            <label className="native-email-field">
              <Mail size={21} strokeWidth={2.35} />
              <input
                type="email"
                name="email_address"
                placeholder={isNewsletter ? "Email address" : "Your email address"}
                aria-label={isNewsletter ? "Email address for Rene's free newsletter" : "Email address for the free beginner guitar course"}
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck="false"
                required
                onChange={(event) =>
                  setEmailReady(/\S+@\S+\.\S+/.test(event.target.value))
                }
              />
            </label>

            <button type="submit" className={emailReady ? "is-ready" : ""} disabled={signupSending}>
              {isNewsletter ? <Mail size={20} strokeWidth={2.5} /> : <Gift size={20} strokeWidth={2.5} />}
              <span>{signupSending ? "Sending…" : buttonLabel || (isNewsletter ? "Join the free newsletter" : "Start the Free Course")}</span>
            </button>
          </form>
        </div>

        <p className="kit-form-consent compact-kit-form-consent">
          {isNewsletter ? "No spam. Unsubscribe anytime." : "No card. No spam. Unsubscribe anytime."}
        </p>

        <InAppBrowserHint />
      </div>

      {showSuccessPopup ? (
        <div className="signup-success-popup" role="status" aria-live="polite">
          <div className="lg-burst" aria-hidden="true">
            {Array.from({ length: 14 }).map((_, index) => (
              <span
                key={index}
                style={{
                  "--burst-angle": `${index * (360 / 14)}deg`,
                  "--burst-delay": `${(index % 4) * 60}ms`,
                }}
              />
            ))}
          </div>

          <button
            type="button"
            className="signup-success-close"
            aria-label="Close message"
            onClick={() => setShowSuccessPopup(false)}
          >
            ×
          </button>

          <div className="signup-success-icon">
            <CheckCircle2 size={28} strokeWidth={2.5} />
          </div>

          <div className="signup-success-copy">
            <h3>{isNewsletter ? "You're on the list" : "Your free course access is ready"}</h3>
            <p>
              {isNewsletter
                ? "Check your inbox to confirm. Then Rene's next lesson will come by email."
                : "Open the interactive course now, then check your inbox for the same link."}
            </p>

            {!isNewsletter && (
              <>
                <span>Save the link so you can come back to your sections and XP.</span>

                <div className="signup-success-actions">
                  <a
                    className="signup-success-course-link primary-action"
                    href={LINKS.members}
                  >
                    Open the Course Now
                    <PlayCircle size={16} strokeWidth={2.35} />
                  </a>

                  <a
                    className="signup-success-course-link secondary-action"
                    href={LINKS.course}
                  >
                    See the Full Course
                    <ArrowRight size={16} strokeWidth={2.35} />
                  </a>
                </div>

                <div className="signup-success-upsell">
                  <strong>Want to keep going after the free course?</strong>
                  <span>
                    The full course • €29 once
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
export function HeroSignupCard({ variant = "course", compact = false, buttonLabel = "" } = {}) {
  const [isAttentionActive, setIsAttentionActive] = useState(false);
  const attentionTimerRef = useRef(null);

  useEffect(() => {
    function handleSignupAttention() {
      if (attentionTimerRef.current) {
        window.clearTimeout(attentionTimerRef.current);
      }
      setIsAttentionActive(false);
      window.requestAnimationFrame(() => {
        setIsAttentionActive(true);
        attentionTimerRef.current = window.setTimeout(() => {
          setIsAttentionActive(false);
        }, 2800);
      });
    }
    window.addEventListener(SIGNUP_ATTENTION_EVENT, handleSignupAttention);
    return () => {
      window.removeEventListener(SIGNUP_ATTENTION_EVENT, handleSignupAttention);
      if (attentionTimerRef.current) {
        window.clearTimeout(attentionTimerRef.current);
      }
    };
  }, []);

  const isNewsletter = variant === "newsletter";
  const trustItems = isNewsletter
    ? [
        { Icon: Sparkles, label: "New lessons & updates" },
        { Icon: Gift, label: "Free offers & discount codes" },
        { Icon: Lock, label: "Unsubscribe anytime" },
      ]
    : [
        { Icon: CheckCircle2, label: "Free, no card" },
        { Icon: Mail, label: "Course link emailed instantly" },
        { Icon: Lock, label: "Unsubscribe anytime" },
      ];

  return (
    <div
      id="hero-signup"
      className={`hero-signup-card ${isAttentionActive ? "signup-attention-active" : ""}`}
    >
      <KitInlineForm variant={variant} buttonLabel={buttonLabel} />
      {!compact && (
        <p
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "6px 14px",
            margin: "12px 0 0",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "rgba(24, 30, 42, 0.6)",
          }}
        >
          {trustItems.map(({ Icon, label }) => (
            <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
              <Icon size={13} strokeWidth={2.5} color="#5E5CE6" /> {label}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function HeroStartPanel() {
  const [showLesson, setShowLesson] = useState(false);

  return (
    <div className="hero-start-panel" aria-label="Free course preview">
      <div className="hero-start-panel-head">
        <span className="hero-start-kicker">Start here</span>
        <strong>Watch a real Day 1 lesson, then start the free course</strong>
      </div>

      <div className="hero-start-media-grid">
        <div className="hero-start-video-card">
          {showLesson ? (
            <iframe
              className="hero-start-video"
              title="Day 1: Posture and guitar parts"
              src="https://www.youtube-nocookie.com/embed/S17YDepd1Lc?autoplay=1"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              className="hero-start-video-poster"
              onClick={() => {
                setShowLesson(true);
                trackEvent("homepage_day1_preview_play");
              }}
              aria-label="Play the Day 1 posture and guitar parts lesson"
            >
              <img
                src="https://i.ytimg.com/vi/S17YDepd1Lc/hqdefault.jpg"
                alt="Rene holding a guitar in the Day 1 posture and guitar parts lesson"
                loading="eager"
                decoding="async"
              />
              <span className="hero-start-play" aria-hidden="true"><PlayCircle size={42} strokeWidth={2.1} /></span>
            </button>
          )}
          <span className="hero-start-label">Day 1: posture</span>
        </div>

        <button
          type="button"
          className="hero-start-game-card"
          onClick={focusHeroSignupEmail}
          aria-label="Start the free guitar course"
        >
          <img
            src={ASSETS.membersPreview}
            alt="Interactive guitar course with XP and achievements"
            loading="eager"
            decoding="async"
          />
          <span className="hero-start-label">XP course</span>
        </button>
      </div>

      <button
        type="button"
        className="hero-start-cta"
        onClick={focusHeroSignupEmail}
      >
        <Gift size={17} strokeWidth={2.5} />
        Start the Free Course
        <ArrowRight size={16} strokeWidth={2.4} />
      </button>
    </div>
  );
}

function CompactCourseSection() {
  return (
    <section
      id="course-section"
      className="content-section compact-course-section"
    >
      <div className="compact-course-card">
        <div className="compact-course-copy">
          <SectionBadge icon={PlayCircle}>Next step</SectionBadge>

          <h2>Want the full beginner path?</h2>

          <p>
            The free course gets you switching your first chords. The full
            course keeps going for 55 more guided days: reading tabs, real
            technique like hammer-ons and muting, your first riff, barre
            chords, lead guitar and fingerstyle, with the practice tools built
            into every lesson.
          </p>

          <div className="compact-course-proof-pill">
            <CheckCircle2 size={16} strokeWidth={2.35} />
            <span>55 more guided days • tools built in • €29 once</span>
          </div>

          <p className="course-price-anchor">
            An hour with an in-person teacher costs €40 or more. This is €29,
            once. Yours to keep, with a 14-day money-back guarantee.
          </p>

          <a
            className="compact-course-image-link"
            href={LINKS.course}
            aria-label="View the full beginner guitar course"
          >
            <img
              src={ASSETS.course}
              alt="Beginner Guitar Course cover"
              loading="eager"
              decoding="async"
            />
          </a>

          <ButtonLink href={LINKS.course} icon={PlayCircle}>
            See the Full Beginner Course
          </ButtonLink>
        </div>
      </div>
    </section>
  );
}

function ConsultationSection() {
  return (
    <section className="content-section consultation-section">
      <div className="consultation-card">
        <div className="consultation-inner">
          <div className="consultation-copy">
            <SectionBadge icon={MessageCircle}>1-on-1 Lessons</SectionBadge>
            <h2>Learn faster with personal guidance</h2>
            <p>
              A private lesson over Google Meet, focused entirely on you. I'll
              hear you play, tell you exactly what to fix, and give you a clear
              plan for what to practice next.
            </p>
            <div className="consultation-details consultation-pricing-row">
              <span>30 min — €35</span>
              <span className="consultation-price-divider">·</span>
              <span>60 min — €60</span>
            </div>
            <a
              className="button-link primary consultation-cta"
              href="https://calendly.com/renemusicacademy/1-on-1-free-online-guitar-consultation"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle size={17} strokeWidth={2.4} />
              Book a Lesson
              <ArrowRight size={16} strokeWidth={2.4} />
            </a>
          </div>
          <img
            src={ASSETS.face}
            alt="Rene from Guitar With Rene"
            className="consultation-face"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </section>
  );
}

function ProofStrip() {
  const points = [
    { Icon: Heart, stat: "161K+", label: "likes on TikTok" },
    { Icon: Users, stat: "2K+", label: "learners following along" },
    { Icon: PlayCircle, stat: "Day 1", label: "video free, no card" },
    { Icon: Guitar, stat: "8 chords", label: "in your first week" },
  ];

  return (
    <section className="content-section home-proof-strip" aria-label="Why beginners start here">
      <div className="home-proof-grid">
        {points.map(({ Icon, stat, label }) => (
          <div className="home-proof-item" key={label}>
            <Icon size={18} strokeWidth={2.4} />
            <strong>{stat}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProTeaserSection() {
  const proPoints = [
    "55 more guided days after Day 7",
    "Song practice, Tone Lab, recording & songwriting",
    "€29 once. No subscription",
  ];

  return (
    <section className="content-section pro-teaser-section" aria-label="Keep going with Pro">
      <div className="pro-teaser-card">
        <div className="pro-teaser-copy">
          <SectionBadge icon={Sparkles}>After the free week</SectionBadge>
          <h2>Keep improving with Pro</h2>
          <p>
            The free 7 day course gets you started. Pro is the full course:
            the rest of the guided path, with songs, tone, recording, and
            songwriting tools in one dashboard. Pay once and keep it.
          </p>
          <ul className="pro-teaser-points">
            {proPoints.map((point) => (
              <li key={point}>
                <CheckCircle2 size={16} strokeWidth={2.35} />
                <span>{point}</span>
              </li>
            ))}
          </ul>
          <a className="button-link primary pro-teaser-cta" href={LINKS.pro}>
            <Sparkles size={16} strokeWidth={2.5} />
            <span>See what Pro unlocks</span>
            <ArrowRight size={15} strokeWidth={2.4} />
          </a>
          <p className="pro-teaser-note">
            New here? Start with the free course above. Pro is for after.
          </p>
        </div>
      </div>
    </section>
  );
}

const CHAT_QUICK_QUESTIONS = [
  "What's in the free plan?",
  "Is this for total beginners?",
  "What does the full course cost?",
  "What is Pro?",
];

function getChatReply(rawText) {
  const text = ` ${String(rawText || "").toLowerCase()} `;
  const has = (...words) => words.some((word) => text.includes(word));

  if (has("pro ", " pro", "membership", "subscription", "subscribe", "monthly", "3.99")) {
    return {
      text: "Pro is the full course for after the free week: €29 once, no subscription. It unlocks 55 more guided days plus song practice, Tone Lab, recording, and songwriting tools, and it stays unlocked. 14-day money-back guarantee.",
    };
  }

  if (has("cost", "price", "€", "eur", "pay", "how much", "expensive", "money")) {
    return {
      text: "The 7 day starter course is free. The full course (Pro) is €29, paid once, no subscription. It adds 55 more guided days with the practice tools built in, and there's a 14-day money-back guarantee.",
      action: "course",
    };
  }

  if (has("course", "full", "15 lesson", "next step")) {
    return {
      text: "The full course picks up after the free week with 55 more guided days in one clear order, for €29 once. Start with the free 7 day course first, then continue when you are ready.",
      action: "course",
    };
  }

  if (has("beginner", "never played", "zero", "no experience", "new to", "first time")) {
    return {
      text: "Yes, this is made for people starting from zero. You do not need to know chords, read music, or understand theory yet.",
      action: "signup",
    };
  }

  if (has("acoustic", "electric", "which guitar", "what guitar", "kind of guitar", "amp", "gear", "equipment")) {
    return {
      text: "Acoustic or electric both work. Any playable guitar is enough for the first 7 days. No amp, pedals, or extra gear needed.",
      action: "signup",
    };
  }

  if (has("which chords", "what chords", "chord")) {
    return {
      text: "You learn 8 chords: Em, G, C and D on days 1 to 3, then Am, Dm, E and A on days 4 to 7. You also practice two strumming patterns and the G, D, Em, C progression.",
      action: "signup",
    };
  }

  if (has("inside", "include", "what do i get", "what exactly", "content", "in the plan", "in the free")) {
    return {
      text: "You get the 7 day course, a Day 1 video, tappable lesson pages, your first 8 chords, two strumming patterns, beginner mistake fixes, a 10 minute session plan, and tab basics.",
      action: "signup",
    };
  }

  if (has("newsletter", "spam", "unsubscribe", "emails")) {
    return {
      text: "It is not just a newsletter. The first thing you get is the interactive course link and starter lesson. After that Rene may send beginner guitar tips, and you can unsubscribe anytime.",
    };
  }

  if (has("after", "happens", "enter my email", "instantly", "inbox")) {
    return {
      text: "The course link is sent instantly and you can start Day 1 right away. No card, no music reading, no spam. Check spam or promotions if you do not see it.",
      action: "signup",
    };
  }

  if (has("video", "day 1", "day one", "lesson")) {
    return {
      text: "Day 1 is a 2 minute starter video where Rene walks you through your first chord diagram, so you know exactly how to use the course. Then each day has one clear 10 minute task.",
      action: "signup",
    };
  }

  if (has("time", "minute", "how long", "busy", "per day")) {
    return {
      text: "Each day is one clear task in about 10 minutes. The point is to stop guessing what to practice, not to practice for hours.",
      action: "signup",
    };
  }

  if (has("theory", "music reading", "read music", "notes", "tab")) {
    return {
      text: "No music reading and no theory overload. You play real basics first: simple chords, easy strums, and easy tab basics.",
      action: "signup",
    };
  }

  if (has("free", "plan", "sign", "start", "get")) {
    return {
      text: "Enter your email on this page and the free 7 day course link is sent instantly. It includes the Day 1 video, 7 practice steps, first chords and strumming. No card, no spam.",
      action: "signup",
    };
  }

  if (has("hello", "hey", " hi ", "yo ")) {
    return {
      text: "Hey! Ask me anything about the free 7 day course or the full course.",
    };
  }

  return {
    text: "I can answer anything on this page: the free 7 day course, what is inside it, or the full course. Try one of the quick questions below.",
  };
}

function StickyCta() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    function handleScroll() {
      // Appear only after the "Your first 7 days are planned" section has
      // scrolled past the top, so it never competes with that section's own
      // CTA. Hide again as the footer comes into view so it stops overlapping
      // the footer links.
      const learn = document.querySelector(".learn-section");
      const footer = document.querySelector(".site-footer");
      const learnPassed = learn
        ? learn.getBoundingClientRect().bottom < 72
        : window.scrollY > 1100;
      const footerNear = footer
        ? footer.getBoundingClientRect().top < window.innerHeight - 40
        : false;
      setIsVisible(learnPassed && !footerNear);
    }

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("has-sticky-cta", isVisible);
    return () => document.body.classList.remove("has-sticky-cta");
  }, [isVisible]);

  return (
    <div
      className={`sticky-cta-bar ${isVisible ? "is-visible" : ""}`}
      aria-hidden={!isVisible}
    >
      <a
        className="sticky-cta-button"
        href={LINKS.heroSignup}
        tabIndex={isVisible ? 0 : -1}
        onClick={focusHeroSignupEmail}
      >
        <Gift size={18} strokeWidth={2.5} />
        <span>Start the Free Course</span>
      </a>
    </div>
  );
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hey, I'm the Guitar With Rene helper. Ask me about the free 7 day course or the full course.",
    },
  ]);
  const listRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;

    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, isOpen]);

  useEffect(() => {
    function handleScroll() {
      setHasScrolled(window.scrollY > 360);
    }

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  function toggleOpen() {
    setIsOpen((current) => {
      const next = !current;

      if (
        next &&
        typeof window !== "undefined" &&
        typeof window.gtag === "function"
      ) {
        window.gtag("event", "chat_open", { send_to: GA_MEASUREMENT_ID });
      }

      return next;
    });
  }

  function askQuestion(question) {
    const reply = getChatReply(question);

    setMessages((current) => [
      ...current,
      { from: "user", text: question },
      { from: "bot", text: reply.text, action: reply.action },
    ]);
  }

  function handleSubmit(event) {
    event.preventDefault();
    const question = inputValue.trim();

    if (!question) {
      return;
    }

    setInputValue("");
    askQuestion(question);
  }

  function handleSignupAction(event) {
    setIsOpen(false);
    focusHeroSignupEmail(event);
  }

  const isWidgetVisible = hasScrolled || isOpen;

  return (
    <div
      className={`gwr-chat-root ${isWidgetVisible ? "is-visible" : "is-hidden"}`}
    >
      {isOpen ? (
        <div
          className="gwr-chat-panel"
          role="dialog"
          aria-label="Guitar With Rene chat helper"
        >
          <div className="gwr-chat-header">
            <img src={ASSETS.face} alt="" aria-hidden="true" />

            <div className="gwr-chat-header-copy">
              <strong>Ask Rene</strong>
              <span>Instant answers from this page</span>
            </div>

            <button
              type="button"
              className="gwr-chat-close"
              aria-label="Close chat"
              onClick={() => setIsOpen(false)}
            >
              <X size={17} strokeWidth={2.4} />
            </button>
          </div>

          <div className="gwr-chat-messages" ref={listRef}>
            {messages.map((message, index) => (
              <div key={index} className={`gwr-chat-row ${message.from}`}>
                <div className={`gwr-chat-bubble ${message.from}`}>
                  <span>{message.text}</span>

                  {message.action === "signup" ? (
                    <button
                      type="button"
                      className="gwr-chat-action"
                      onClick={handleSignupAction}
                    >
                      <Gift size={15} strokeWidth={2.5} />
                      Start the Free Course
                    </button>
                  ) : null}

                  {message.action === "course" ? (
                    <a
                      className="gwr-chat-action"
                      href={LINKS.course}
                    >
                      <PlayCircle size={15} strokeWidth={2.5} />
                      See the Full Course
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <div className="gwr-chat-chips">
            {CHAT_QUICK_QUESTIONS.map((question) => (
              <button
                key={question}
                type="button"
                onClick={() => askQuestion(question)}
              >
                {question}
              </button>
            ))}
          </div>

          <form className="gwr-chat-input-row" onSubmit={handleSubmit}>
            <input
              type="text"
              value={inputValue}
              placeholder="Ask about the plan or course…"
              aria-label="Type your question"
              onChange={(event) => setInputValue(event.target.value)}
            />

            <button type="submit" aria-label="Send question">
              <Send size={17} strokeWidth={2.4} />
            </button>
          </form>
        </div>
      ) : null}

      <button
        type="button"
        className={`gwr-chat-fab ${isOpen ? "is-open" : ""}`}
        aria-label={isOpen ? "Close chat helper" : "Open chat helper"}
        aria-expanded={isOpen}
        onClick={toggleOpen}
      >
        {isOpen ? (
          <X size={24} strokeWidth={2.3} />
        ) : (
          <MessageCircle size={24} strokeWidth={2.3} />
        )}
      </button>
    </div>
  );
}

function ToolShowcaseSection() {
  return (
    <section className="content-section hp-tools-section" aria-label="Practice tools included">
      <div className="hp-tools-head">
        <SectionBadge icon={Sparkles}>Practice tools</SectionBadge>
        <h2>Tools that help you practice</h2>
        <p>Tune, keep time, hear chords, and track your progress in one place.</p>
      </div>

      <div className="hp-tools-grid">
        {/* Tuner */}
        <article className="hp-tool-card">
          <div className="hp-tool-visual hp-tuner-visual" aria-hidden="true">
            <div className="hp-tuner-note">E<small>2</small></div>
            <div className="hp-tuner-meter">
              <span className="hp-tuner-zero" />
              <span className="hp-tuner-needle" />
            </div>
            <span className="hp-tuner-status">In tune</span>
            <div className="hp-tuner-strings">
              {["E", "A", "D", "G", "B", "e"].map((s) => <span key={s}>{s}</span>)}
            </div>
          </div>
          <h3>Tuner</h3>
          <p>Use your ear or your mic. The display turns green when you are in tune.</p>
        </article>

        {/* Metronome */}
        <article className="hp-tool-card">
          <div className="hp-tool-visual hp-metro-visual" aria-hidden="true">
            <div className="hp-metro-dots">
              <span /><span /><span /><span />
            </div>
            <div className="hp-metro-bpm">90<small>BPM</small></div>
            <div className="hp-metro-bar"><i /></div>
          </div>
          <h3>Metronome</h3>
          <p>Build steadier timing and make chord changes feel less choppy.</p>
        </article>

        {/* Chord library */}
        <article className="hp-tool-card">
          <div className="hp-tool-visual hp-chord-visual" aria-hidden="true">
            <svg viewBox="0 0 80 100" className="hp-chord-svg" role="img">
              <rect x="14" y="17" width="52" height="3" rx="1" className="hp-c-nut" />
              {[20, 36, 52, 68, 84].map((y) => (
                <line key={y} x1="14" y1={y} x2="66" y2={y} className="hp-c-fret" />
              ))}
              {[14, 24, 34, 44, 54, 64].map((x) => (
                <line key={x} x1={x} y1="20" x2={x} y2="84" className="hp-c-string" />
              ))}
              {/* opens: D, G, B */}
              {[34, 44, 54].map((x) => <circle key={x} cx={x} cy="11" r="3" className="hp-c-open" />)}
              {/* dots: lowE f3, A f2, e f3 */}
              <circle cx="14" cy="60" r="6" className="hp-c-dot" />
              <circle cx="24" cy="44" r="6" className="hp-c-dot" />
              <circle cx="64" cy="60" r="6" className="hp-c-dot" />
            </svg>
            <span className="hp-chord-name">G</span>
            <span className="hp-chord-play">Hear it</span>
          </div>
          <h3>Chord library</h3>
          <p>Look up any chord and tap to hear exactly how it should sound.</p>
        </article>

        {/* XP / streaks */}
        <article className="hp-tool-card">
          <div className="hp-tool-visual hp-xp-visual" aria-hidden="true">
            <div className="hp-xp-top">
              <span className="hp-xp-coin"><Coins size={16} strokeWidth={2.4} /></span>
              <span className="hp-xp-num">350</span>
              <span className="hp-xp-streak"><Flame size={14} strokeWidth={2.4} /> 3 day streak</span>
            </div>
            <div className="hp-xp-bar"><i /></div>
            <div className="hp-xp-badges">
              <span><Guitar size={15} strokeWidth={2.3} /></span>
              <span><Hand size={15} strokeWidth={2.3} /></span>
              <span><Drum size={15} strokeWidth={2.3} /></span>
              <span className="dim"><GraduationCap size={15} strokeWidth={2.3} /></span>
              <span className="dim"><Crown size={15} strokeWidth={2.3} /></span>
            </div>
          </div>
          <h3>XP and streaks</h3>
          <p>Small wins are tracked so it feels easier to come back tomorrow.</p>
        </article>
      </div>
    </section>
  );
}

function HomePage() {
  useEffect(() => {
    document.title = "Free 7 Day Beginner Guitar Course | Guitar With Rene";

    const description =
      "Start a free 7 day beginner guitar course with a Day 1 video, first chords, simple strums, and practice tools.";

    const canonicalUrl = "https://www.guitarwithrene.com/welcome";

    const freePackPreload = new Image();
    freePackPreload.src = ASSETS.freePack;

    const metaDescription = document.querySelector('meta[name="description"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector(
      'meta[property="og:description"]',
    );
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector(
      'meta[name="twitter:description"]',
    );

    metaDescription?.setAttribute("content", description);
    canonical?.setAttribute("href", canonicalUrl);

    ogTitle?.setAttribute(
      "content",
      "Free 7 Day Beginner Guitar Course | Guitar With Rene",
    );
    ogDescription?.setAttribute("content", description);
    ogUrl?.setAttribute("content", canonicalUrl);

    twitterTitle?.setAttribute(
      "content",
      "Free 7 Day Beginner Guitar Course | Guitar With Rene",
    );
    twitterDescription?.setAttribute("content", description);

    if (typeof window.gtag === "function") {
      window.gtag("event", "design_variant_view", {
        send_to: GA_MEASUREMENT_ID,
        design_variant: getDesignVariant(),
      });
    }
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const sections = document.querySelectorAll(".content-section");

    sections.forEach((section) => section.classList.add("lg-reveal"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("lg-reveal-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    let frame = null;

    function handleScroll() {
      if (frame !== null) {
        return;
      }

      frame = requestAnimationFrame(() => {
        document.documentElement.style.setProperty(
          "--lg-scroll",
          String(window.scrollY),
        );
        frame = null;
      });
    }

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, []);

  const designVariant = getDesignVariant();

  return (
    <div
      className={`page-shell light-page home-page ${designVariant === "warm" ? "lg-warm" : ""}`}
    >
      <div className="lg-ambient" aria-hidden="true">
        <span className="lg-blob lg-blob-1" />
        <span className="lg-blob lg-blob-2" />
        <span className="lg-blob lg-blob-3" />
      </div>

      <main className="site-container">
       <section className="hero-section">
  <div className="hero-copy-col">
    <div className="hero-top-pills">
      <div className="brand-line hero-plan-badge">
        <Sparkles size={15} strokeWidth={2.6} />
        <span>Free 7 day guitar course</span>
      </div>

      <a
        className="hero-social-proof"
        href="https://www.tiktok.com/@guitarwithrene"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Guitar With Rene on TikTok"
      >
        <Music2 size={15} strokeWidth={2.4} />
        <span>
          <AnimatedCount target={161} suffix="K+" /> likes on&nbsp;TikTok
        </span>
      </a>
    </div>

    <h1>Know what to practice today</h1>

    <p className="hero-subhead">
      One clear 10 minute task each day. Learn your first chords, strum
      with real songs, and track progress as you go.
    </p>

    <HeroSignupCard />
  </div>

  <div className="hero-visual-col">
    <HeroStartPanel />
  </div>
</section>

        <ProofStrip />

        <LearnSection />

        <CompactCourseSection />

        <ProTeaserSection />

        <ToolShowcaseSection />

        <BeginnerTrustSection />

        <ConsultationSection />

        <section className="content-section faq-section compact-section">
          <SectionBadge icon={CheckCircle2}>Questions</SectionBadge>
          <h2>Quick answers</h2>

          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <FAQCard
                key={item.question}
                question={item.question}
                answer={item.answer}
                defaultOpen={index === 0}
              />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />

      <StickyCta />

      <ChatWidget />
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useState(getStoredTheme);
  const pathname = window.location.pathname;
  useAccountAutosave();

  useEffect(() => {
    function captureInstallPrompt(event) {
      event.preventDefault();
      window.__gwrInstallPrompt = event;
      window.dispatchEvent(new CustomEvent("gwr-install-ready", { detail: event }));
    }
    window.addEventListener("beforeinstallprompt", captureInstallPrompt);
    if (import.meta.env.PROD && "serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        // Installation is optional; the website remains fully usable.
      });
    }
    return () => window.removeEventListener("beforeinstallprompt", captureInstallPrompt);
  }, []);

  // Funnel tracking: one delegated listener catches every click on a link to the
  // paid offer (/pro, formerly the Gumroad course), wherever it appears.
  useEffect(() => {
    function onDocClick(event) {
      const link = event.target.closest ? event.target.closest("a[href]") : null;
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (href === "/pro" || href.startsWith("/pro#") || href.includes("gumroad.com")) {
        trackEvent("pro_offer_click", { page: window.location.pathname });
      }
    }
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, []);

  useEffect(() => {
    if (!supabase || window.location.pathname !== "/") return undefined;

    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const pendingAccountLogin = sessionStorage.getItem("gwrene_account_login_pending") === "1";
    const looksLikeAuthReturn = pendingAccountLogin
      || hash === "#"
      || hash.includes("access_token")
      || hash.includes("refresh_token")
      || hash.includes("type=signup")
      || search.includes("code=")
      || search.includes("token_hash");

    if (!looksLikeAuthReturn) return undefined;

    let didRedirect = false;
    const sendToMember = () => {
      if (didRedirect) return;
      didRedirect = true;
      sessionStorage.removeItem("gwrene_account_login_pending");
      window.location.replace("/member");
    };

    const sessionCheck = window.setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user) {
        sendToMember();
      } else {
        sessionStorage.removeItem("gwrene_account_login_pending");
      }
    }, 350);

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user && (event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
        sendToMember();
      }
    });

    return () => {
      window.clearTimeout(sessionCheck);
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    applyTheme(theme);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage errors.
    }
  }, [theme]);

  useEffect(() => {
    const pageViewTimer = window.setTimeout(() => {
      sendAnalyticsPageView();
    }, 120);

    return () => {
      window.clearTimeout(pageViewTimer);
    };
  }, [pathname]);

  function toggleTheme() {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  }

  /* 2026-07-31: the React versions of /easy-guitar-songs/, /easy-guitar-tabs/,
     /beginner-guitar-chords/, /how-to-read-guitar-tabs/, /guitar-practice-plan/
     and /about-rene/ were REMOVED, along with src/pages/ResourcePages.jsx.

     Those six URLs are also generated as static HTML by
     scripts/generate-seo-pages.mjs, and every link to them in this app is a
     plain <a href> — a full page load. Vercel checks the filesystem before the
     SPA rewrite, so production always served the static page and this branch
     was unreachable. Two copies of six pages existed, only one of which anyone
     could actually reach, and they had already drifted apart.

     The static versions are also the better ones: they carry FAQ blocks,
     JSON-LD, and song/tab lists generated from the JSON data.

     If you want to change what these URLs say, edit generate-seo-pages.mjs.
     Do NOT re-add a React route here — see the serveStaticIndexes plugin in
     vite.config.js, which makes dev serve the same static file production does. */

  if (pathname === "/privacy" || pathname === "/privacy/") {
    return (
      <div className="page-shell light-page doc-shell">
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><PrivacyPolicy /></Suspense>
        <SiteFooter />
      </div>
    );
  }

  if (pathname === "/terms" || pathname === "/terms/") {
    return (
      <div className="page-shell light-page doc-shell">
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><TermsOfService /></Suspense>
        <SiteFooter />
      </div>
    );
  }

  if (
    pathname === "/member/course" ||
    pathname === "/member/course/"
  ) {
    window.location.replace("/member/course/free");
    return null;
  }

  if (
    pathname === "/member" ||
    pathname === "/member/" ||
    pathname === "/members" ||
    pathname === "/members/" ||
    pathname.startsWith("/member/") ||
    pathname.startsWith("/members/")
  ) {
    return (
      <>
        <CookieConsent />
        <Suspense fallback={<MemberRouteFallback />}>
          <Members memberPath={pathname} theme={theme} onToggleTheme={toggleTheme} />
        </Suspense>
      </>
    );
  }

  if (
    pathname === "/account" ||
    pathname === "/account/"
  ) {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        {readProCheckoutIntent() && <ProCheckoutResumeStandalone showSignInHint />}
        <AccountPage />
      </>
    );
  }

  if (
    pathname === "/pro" ||
    pathname === "/pro/" ||
    pathname === "/members-pro" ||
    pathname === "/members-pro/"
  ) {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={<MemberRouteFallback />}><MembersPro /></Suspense>
      </>
    );
  }

  if (
    pathname === "/free-lesson" ||
    pathname === "/free-lesson/" ||
    pathname === "/course-preview" ||
    pathname === "/course-preview/"
  ) {
    window.location.replace("/beginner-guitar-course/");
    return null;
  }

  if (pathname === "/tuner" || pathname === "/tuner/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><TunerPage /></Suspense>
      </>
    );
  }

  if (pathname === "/metronome" || pathname === "/metronome/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><MetronomePage /></Suspense>
      </>
    );
  }

  if (pathname === "/tap-tempo" || pathname === "/tap-tempo/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><TapTempoPage /></Suspense>
      </>
    );
  }

  if (pathname === "/piano-guitar-notes" || pathname === "/piano-guitar-notes/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><PianoGuitarNotesPage /></Suspense>
      </>
    );
  }

  if (pathname === "/vocal-range-guitar-key-finder" || pathname === "/vocal-range-guitar-key-finder/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><VocalRangeGuitarKeyPage /></Suspense>
      </>
    );
  }

  if (pathname === "/guitar-chord-buzzing-fixer" || pathname === "/guitar-chord-buzzing-fixer/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><GuitarChordBuzzingFixerPage /></Suspense>
      </>
    );
  }

  if (pathname === "/one-minute-chord-changes" || pathname === "/one-minute-chord-changes/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><OneMinuteChordChangesPage /></Suspense>
      </>
    );
  }

  if (pathname === "/chords" || pathname === "/chords/") {
    return (
      <>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <CookieConsent />
        <Suspense fallback={null}><ChordsPage /></Suspense>
      </>
    );
  }

  if (pathname === "/welcome" || pathname === "/welcome/") {
    return (
      <>
        <HomePage />
        <CookieConsent />
      </>
    );
  }

  return (
    <>
      <CookieConsent />
      <Suspense fallback={<MemberRouteFallback />}>
        <Members memberPath="/member/today" homepage theme={theme} onToggleTheme={toggleTheme} />
      </Suspense>
    </>
  );
}
